import { useAtomValue, useSetAtom } from "jotai";
import { zslToCanvasPostCommand } from "@zapier/streaming";
import { useReactFlow } from "@xyflow/react";
import {
  canvasAtom,
  edgesAtom,
  nodesAtom,
  zslAtom,
} from "@zapier/canvas-state";
import { validateZSL } from "../Composer/validateZSL";
import { Composer } from "../Composer/Composer";

type Props = {
  onClose?: () => void;
};

export function CanvasComposer({ onClose }: Props) {
  const convertZslToCanvas = useSetAtom(zslToCanvasPostCommand);
  const { fitView } = useReactFlow();
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const zsl = useAtomValue(zslAtom);
  const canvas = useAtomValue(canvasAtom);

  const handleSubmitZslToCanvas = async (zsl: any) => {
    try {
      const nodesAndEdges = await convertZslToCanvas(JSON.stringify(zsl), true);
      if (nodesAndEdges) {
        const { nodes, edges } = nodesAndEdges;
        setNodes(nodes);
        setEdges(edges);
        fitView();
      }
    } catch (error) {
      console.error("Error converting ZSL to Canvas:", error);
    }
  };

  return (
    <Composer
      orchestratorHost="canvas"
      onClose={onClose}
      onToolCall={async ({ toolCall }) => {
        if (toolCall.toolName === "update_ui") {
          await handleSubmitZslToCanvas(toolCall.args);
          const validationResult = await validateZSL(toolCall.args as any);

          if (!validationResult.valid) {
            const warningMessage =
              validationResult.errorMessage || "Validation failed";
            (toolCall as any).warningMessage = warningMessage;

            return {
              toolCallId: toolCall.toolCallId,
              result: {
                content: [
                  {
                    type: "text",
                    text: `Canvas updated but validation failed.\n${warningMessage}`,
                  },
                ],
              },
            };
          } else {
            (toolCall as any).isValidationSuccess = true;
            return {
              toolCallId: toolCall.toolCallId,
              result: {
                content: [{ type: "text", text: "Canvas updated" }],
              },
            };
          }
        }
        throw new Error(`Unhandled tool call: ${toolCall.toolName}`);
      }}
      additionalData={{
        ...(zsl && { zslTemplate: JSON.stringify(zsl) }),
        ...(canvas && { canvas: JSON.stringify(canvas) }),
      }}
    />
  );
}

export default CanvasComposer;
